import { DialogComponent } from '@theme/dialog';
import { ThemeEvents, CompareUpdateEvent } from '@theme/events';
import { ProductCompare } from '@theme/product-compare';

/**
 * A custom element that manages the compare products drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} items - The list of compared products.
 * @property {HTMLElement} emptyState - The empty state message, shown with zero products.
 * @property {HTMLElement} addMoreMessage - Shown instead of emptyState with exactly one product.
 * @property {HTMLTemplateElement} itemTemplate - The template for a single compared product.
 * @property {HTMLElement} compareBubble - The count bubble next to the drawer heading.
 * @property {HTMLElement} compareBubbleCount - The count text within the heading bubble.
 * @property {HTMLElement} clearButton - Clears the list; hidden with zero products.
 * @property {HTMLElement} compareNowButton - Links to the compare page; shown with two or more products.
 * @property {HTMLElement} searchButton - Opens site search; shown with fewer than two products.
 *
 * @extends {DialogComponent}
 */
class CompareDrawerComponent extends DialogComponent {
  requiredRefs = [
    'dialog',
    'items',
    'emptyState',
    'addMoreMessage',
    'itemTemplate',
    'compareBubble',
    'compareBubbleCount',
    'clearButton',
    'compareNowButton',
    'searchButton',
  ];

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.compareUpdate, this.#handleCompareUpdate);
    this.#render(ProductCompare.getProducts());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.compareUpdate, this.#handleCompareUpdate);
  }

  open() {
    this.showDialog();
  }

  close() {
    this.closeDialog();
  }

  /**
   * Re-syncs with the compare list when re-rendered by the Section Rendering API
   * (e.g. the header's post-load hydration fetch, see sections/header.liquid).
   * The freshly-fetched markup has no knowledge of the localStorage-backed compare
   * list, so it always morphs in empty unless corrected here.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#render(ProductCompare.getProducts());
  }

  /**
   * Clears the entire compare list.
   */
  clearAll() {
    ProductCompare.clearProducts();
  }

  /**
   * @param {CompareUpdateEvent} event - The compare update event.
   */
  #handleCompareUpdate = (event) => {
    this.#render(event.detail.products);

    // Auto-open once the flying icon has had time to land, mirroring how the cart
    // drawer opens after CartAddEvent (see assets/cart-drawer.js #handleCartAdd).
    // Gated by the `auto-open` attribute, set from settings.auto_open_compare_drawer.
    if (event.detail.action === 'add' && this.hasAttribute('auto-open')) {
      setTimeout(() => this.open(), 500);
    }
  };

  /**
   * @param {import('@theme/product-compare').CompareProduct[]} products
   */
  #render(products) {
    const {
      items,
      emptyState,
      addMoreMessage,
      itemTemplate,
      compareBubble,
      compareBubbleCount,
      clearButton,
      compareNowButton,
      searchButton,
    } = this.refs;
    const count = products.length;

    items.replaceChildren();
    emptyState.classList.toggle('hidden', count > 0);
    // Comparing needs at least two products — shown instead of emptyState when there's
    // exactly one, nudging toward adding more rather than looking like a dead end.
    addMoreMessage.classList.toggle('hidden', count !== 1);

    compareBubbleCount.textContent = String(count);
    compareBubble.classList.toggle('hidden', count === 0);

    // Nothing to clear yet with zero products. "Compare now" only makes sense with two or
    // more; below that, send them to search instead of a comparison that can't happen yet.
    clearButton.classList.toggle('hidden', count === 0);
    compareNowButton.classList.toggle('hidden', count < 2);
    searchButton.classList.toggle('hidden', count >= 2);

    for (const product of products) {
      const fragment = /** @type {DocumentFragment} */ (itemTemplate.content.cloneNode(true));
      const link = fragment.querySelector('[ref="itemLink"]');
      const image = fragment.querySelector('[ref="itemImage"]');
      const title = fragment.querySelector('[ref="itemTitle"]');
      const price = fragment.querySelector('[ref="itemPrice"]');
      const compareAtPrice = fragment.querySelector('[ref="itemCompareAtPrice"]');
      const removeButton = fragment.querySelector('[ref="itemRemove"]');

      if (link instanceof HTMLAnchorElement) link.href = `/products/${product.handle}`;
      if (image instanceof HTMLImageElement && product.image) {
        image.src = product.image;
        image.alt = product.title;
      }
      if (title) title.textContent = product.title;
      if (price) price.textContent = product.price;
      if (compareAtPrice) {
        compareAtPrice.textContent = product.compareAtPrice ?? '';
        compareAtPrice.classList.toggle('hidden', !product.compareAtPrice);
      }
      // Wired directly (rather than via the declarative `on:click` attribute) because that
      // system parses query-string data with `Number()`, which would corrupt numeric product IDs.
      if (removeButton) {
        removeButton.addEventListener('click', (event) => {
          // Removing the product re-renders the list, detaching this button before the click
          // finishes bubbling to the dialog's outside-click handler — which would otherwise read
          // the now-detached target as "outside" and close the drawer. Stop it reaching that far.
          event.stopPropagation();
          ProductCompare.removeProduct(product.id);
        });
      }

      items.append(fragment);
    }
  }
}

if (!customElements.get('compare-drawer-component')) {
  customElements.define('compare-drawer-component', CompareDrawerComponent);
}
